function escapeHtml(text: string): string {
  return text.replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

const PRINT_STYLES = `
  @page {
    size: A4 portrait;
    margin: 20mm;
  }
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    background: #fff;
    color: #000;
  }
  body {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 12pt;
    line-height: 1.55;
    width: 100%;
  }
  .print-title {
    font-size: 14pt;
    font-weight: 700;
    margin: 0 0 14pt;
    line-height: 1.35;
  }
  .print-body {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    word-wrap: break-word;
    font-size: 12pt;
    line-height: 1.55;
    margin: 0;
  }
  @media print {
    html, body {
      width: auto;
      height: auto;
      overflow: visible;
    }
  }
`;

/** ~A4 width at 96dpi — used only to lay out the hidden iframe before print. */
const PRINT_LAYOUT_WIDTH_PX = 794;

/** Print a title + plain-text body in a hidden iframe (stays on the same page). */
export function printTextDocument(title: string, body: string, onError?: () => void): void {
  const safeTitle = escapeHtml(title);
  const safeBody = escapeHtml(body);
  const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${safeTitle}</title>
  <style>${PRINT_STYLES}</style>
</head>
<body>
  <div class="print-title">${safeTitle}</div>
  <div class="print-body">${safeBody}</div>
</body>
</html>`;

  printHtml(html, { onError });
}

/**
 * Print HTML via a hidden off-screen iframe on the current page.
 * Does not open a new tab. Avoids iframe \`onload\` (Chrome can fire it on a blank
 * frame before \`document.write\` finishes); prints after write + layout delay.
 */
export function printHtml(html: string, options?: { onError?: () => void }): void {
  const iframe = document.createElement('iframe');
  iframe.title = 'Print';
  iframe.setAttribute('aria-hidden', 'true');
  // Match content width; height is set from document after layout (fixed A4 height shrinks print output).
  iframe.style.cssText = [
    'position:fixed',
    'left:0',
    'top:0',
    `width:${PRINT_LAYOUT_WIDTH_PX}px`,
    'height:0',
    'border:0',
    'visibility:hidden',
    'overflow:hidden',
  ].join(';');

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    try {
      iframe.remove();
    } catch {
      // ignore
    }
  };

  const fail = () => {
    cleanup();
    options?.onError?.();
  };

  document.body.appendChild(iframe);

  const frameWin = iframe.contentWindow;
  const doc = frameWin?.document;
  if (!frameWin || !doc) {
    fail();
    return;
  }

  doc.open();
  doc.write(html);
  doc.close();

  const triggerPrint = () => {
    const bodyEl = doc.body;
    const text = bodyEl?.innerText?.trim() ?? '';
    if (!bodyEl || !text) {
      fail();
      return;
    }

    const contentHeight = Math.ceil(
      Math.max(bodyEl.scrollHeight, bodyEl.offsetHeight, bodyEl.getBoundingClientRect().height),
    );
    iframe.style.height = `${Math.max(contentHeight, 1)}px`;

    try {
      frameWin.focus();
      frameWin.print();
      frameWin.addEventListener('afterprint', cleanup, { once: true });
      window.setTimeout(cleanup, 60_000);
    } catch {
      fail();
    }
  };

  // Let the browser finish parsing/layout before opening the print preview.
  window.setTimeout(triggerPrint, 300);
}
